import { inputAdornmentClasses } from '@mui/material';
import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import { SxProps, Theme } from '@mui/system';
import { DatePicker, PickersShortcutsItem } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { get, isNil } from 'lodash-es';
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
} from 'react-hook-form';

import { desktopActionBar } from '~components/FormFields/desktopActionBar';
import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';

type DayjsTzInput = Parameters<typeof dayjs.tz>[0];

const convertToDayjs = (input: Dayjs | DayjsTzInput) => {
  const inputAsDayjs = dayjs.isDayjs(input) ? input : dayjs.tz(input);

  return inputAsDayjs;
};

const isValidDate = (date: Dayjs | null): date is Dayjs => {
  if (isNil(date)) {
    return false;
  }

  const isValid = date.isValid() && date.year() > 2000;

  return isValid;
};

const parseFieldValueAsDayjs = (fieldValue: unknown) => {
  if (!fieldValue) {
    return null;
  }

  const valueAsDayjs = convertToDayjs(fieldValue as DayjsTzInput);

  return isValidDate(valueAsDayjs) ? valueAsDayjs : null;
};

const shortcutsItems: PickersShortcutsItem<Dayjs>[] = [
  { label: t('date.today'), getValue: () => dayjs.tz().startOf('day') },
  {
    label: t('date.tomorrow'),
    getValue: () => dayjs().tz().add(1, 'day').startOf('day'),
  },
];

interface SingleDateFormFieldProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  dataUi?: string;
  disabled?: boolean;
  errors?: FieldErrors<TFieldValues>;
  isRequired?: boolean;
  label?: string;
  name: string;
  sx?: SxProps<Theme>;
}

export default function SingleDateFormField<
  TFieldValues extends FieldValues = FieldValues,
>({
  control,
  dataUi,
  disabled,
  errors,
  isRequired,
  label,
  name,
  sx,
}: SingleDateFormFieldProps<TFieldValues>) {
  const dataUiProps = dataUi ? { 'data-ui': dataUi } : {};
  return (
    <Controller
      control={control}
      name={name as FieldPath<TFieldValues>}
      render={({ field }) => {
        // Plain computation — a useMemo here would run inside Controller's
        // render prop, outside a component/hook body (Rules of Hooks).
        const value = parseFieldValueAsDayjs(field.value);

        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              width: '100%',
            }}
          >
            <FormGroup sx={{ width: '100%' }}>
              {label && <FormFieldLabel label={`${label}${isRequired ? ' *' : ''}`} />}

              <DatePicker
                {...field}
                enableAccessibleFieldDOMStructure={false}
                value={value}
                closeOnSelect={false}
                slotProps={{
                  actionBar: desktopActionBar([]),
                  shortcuts: { items: shortcutsItems },
                  textField: {
                    className: isRequired ? 'required' : '',
                    error: Boolean(get(errors, name)),
                    fullWidth: true,
                    slotProps: {
                      htmlInput: {
                        sx: { fontSize: '12px', height: '23px' },
                        ...dataUiProps,
                      },
                    },
                    placeholder: 'mm/dd/yyyy',
                    required: isRequired,
                    variant: 'outlined',
                    sx: { ...sx, [`& .${inputAdornmentClasses.root}`]: { ml: 0 } },
                  },
                }}
                disabled={Boolean(disabled)}
                onChange={(date: Dayjs | null) => {
                  const isValid = isValidDate(date);

                  if (isValid) {
                    field.onChange(date.format('YYYY-MM-DD'));
                  } else {
                    field.onChange(null);
                  }
                }}
              />
            </FormGroup>
          </Box>
        );
      }}
    />
  );
}
